import React, { useState, useContext } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { AppStateContext } from "../../contexts/WorkoutContext";
import packageInfo from "../../../package.json";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { User, LogOut, Settings, LogIn, CircleHelp, SlidersHorizontal, Smartphone } from "lucide-react";
import { openHelp } from "../../utils/helpBus";
import ChangelogModal from "./ChangelogModal";
import {
  isGoogleOAuthConfigured,
  getGoogleAuthUrl,
} from "../../services/googleOAuth";
import { useToast } from "../../contexts/ToastContext";
import { isAndroidBrowser, isNativeApp } from "../../platform/native";
import { signInNatively } from "./nativeSignIn";
import useUpdateWaiting from "../../hooks/useUpdateWaiting";
import useUiMode from "../../hooks/useUiMode";

// On an Android phone's browser, points to the Android app's download and
// install steps (issue #217). Hidden everywhere else, and inside the app.
function AndroidAppItem() {
  if (!isAndroidBrowser()) return null;
  return (
    <DropdownMenuItem onClick={() => openHelp("android-app#download")}>
      <Smartphone className="mr-2 h-4 w-4" />
      <span>Get the Android app</span>
    </DropdownMenuItem>
  );
}

// The Basic / Advanced switch (#172), next to Profile and Settings so it
// reads as app-wide rather than one screen's setting. The menu stays open so
// the new state shows.
function ModeItem() {
  const { advanced, setAdvanced } = useUiMode();
  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        setAdvanced(!advanced);
      }}
    >
      <SlidersHorizontal className="mr-2 h-4 w-4" />
      <span className="flex-1">Advanced options</span>
      <span className={advanced ? "text-xs font-semibold text-primary" : "text-xs text-muted-foreground"}>
        {advanced ? "On" : "Off"}
      </span>
    </DropdownMenuItem>
  );
}

function MenuFooter({ onOpenChangelog }) {
  // The badge keeps naming the build actually running — that is the number
  // worth quoting in a bug report. The dot is what tells you it is no longer
  // the newest one, and the changelog behind it is where you take the update.
  const updateWaiting = useUpdateWaiting();

  return (
    <>
      <DropdownMenuSeparator />

      <div className="px-2 pb-2 pt-1">
        <button
          type="button"
          onClick={onOpenChangelog}
          title={
            updateWaiting
              ? "A newer build is ready — open the changelog to reload"
              : "Open the changelog"
          }
          className="flex items-center gap-1.5 px-2 py-1 font-mono text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          v{packageInfo.version}
          {updateWaiting && (
            <>
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full bg-primary"
              />
              <span className="sr-only">Update available</span>
            </>
          )}
        </button>
      </div>
    </>
  );
}

export function UserMenu() {
  const { user, isAuthenticated, logout, login } = useAuth();
  const { setView } = useContext(AppStateContext);
  const toast = useToast();
  const googleConfigured = isGoogleOAuthConfigured();
  const [isLoading, setIsLoading] = useState(false);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const displayName = user?.displayName || user?.name || "Guest";

  // Handle sign in with redirect
  const handleSignIn = async () => {
    // In the Android app the redirect would finish in Chrome, not the app.
    if (isNativeApp()) {
      setIsLoading(true);
      try {
        await signInNatively({ login, toast });
      } finally {
        setIsLoading(false);
      }
      return;
    }
    setIsLoading(true);
    try {
      toast.showToast({
        title: "Redirecting to Google",
        description: "Complete sign-in in Google to finish connecting Khronos.",
        duration: 3500,
      });
      const authUrl = await getGoogleAuthUrl();
      if (authUrl) {
        // Redirect to Google OAuth
        window.location.href = authUrl;
        return; // Keep loading state during redirect
      }
      console.error("Failed to get Google auth URL");
      toast.showToast({
        title: "Sign-in failed",
        description:
          "Khronos could not start Google sign-in. Please try again.",
        duration: 7000,
      });
    } catch (error) {
      console.error("Error starting Google sign in:", error);
      toast.showToast({
        title: "Sign-in failed",
        description: error.message || "Khronos could not start Google sign-in.",
        duration: 7000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Always show user icon
  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            aria-label="User menu"
          >
            {isAuthenticated && user?.photoUrl ? (
              <img
                src={user.photoUrl}
                alt={user.name || user.email}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <User className="h-5 w-5" />
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-56">
          {isAuthenticated && user ? (
            <>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {displayName}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user.email}
                  </p>
                  <p className="text-[11px] leading-none text-muted-foreground">
                    Account, profile, and app settings
                  </p>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={() => setView("profile")}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => setView("settings")}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>

              <ModeItem />

              <DropdownMenuItem onClick={() => openHelp()}>
                <CircleHelp className="mr-2 h-4 w-4" />
                <span>Help</span>
              </DropdownMenuItem>

              <AndroidAppItem />

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={logout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>

              <MenuFooter onOpenChangelog={() => setIsChangelogOpen(true)} />
            </>
          ) : (
            <>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">Guest</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    Sign in to sync your data
                  </p>
                  <p className="text-[11px] leading-none text-muted-foreground">
                    Profile and settings stay on this device
                  </p>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              {googleConfigured ? (
                <DropdownMenuItem onClick={handleSignIn} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      <span>Sign in with Google</span>
                    </>
                  )}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem disabled>
                  <span className="text-muted-foreground">
                    Google Sign-In not configured
                  </span>
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={() => setView("profile")}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => setView("settings")}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>

              <ModeItem />

              <DropdownMenuItem onClick={() => openHelp()}>
                <CircleHelp className="mr-2 h-4 w-4" />
                <span>Help</span>
              </DropdownMenuItem>

              <AndroidAppItem />

              <MenuFooter onOpenChangelog={() => setIsChangelogOpen(true)} />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangelogModal
        open={isChangelogOpen}
        onOpenChange={setIsChangelogOpen}
      />
    </>
  );
}

export default UserMenu;
